'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

export default function Header({ className = '' }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasUserSession, setHasUserSession] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const isPortalPage = pathname?.startsWith('/my-portal');

  const handlePortalLogout = () => {
    try {
      localStorage.removeItem('parkping_user_token');
    } catch {
      // ignore
    }
    router.push('/user-login');
  };

  const navigationItems = [
    { label: 'Home', href: '/homepage' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Product', href: '/product-showcase' },
    { label: 'Pricing', href: '/pricing-plans' },
    { label: 'Get Started', href: '/get-started' },
  ];

  const moreItems = [{ label: 'Support Hub', href: '/support-hub' }];

  useEffect(() => {
    const syncSession = () => {
      try {
        setHasUserSession(Boolean(localStorage.getItem('parkping_user_token')));
      } catch {
        setHasUserSession(false);
      }
    };

    syncSession();
    window.addEventListener('storage', syncSession);
    return () => window.removeEventListener('storage', syncSession);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70 ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link
            href="/homepage"
            className="flex items-center transition-transform duration-300 hover:scale-[1.01]"
          >
            <Image
              src="/logo.png"
              alt="ParkPing Logo"
              width={160}
              height={80}
              priority
              className="object-contain"
            />
          </Link>

          {!isPortalPage && <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-all duration-300 rounded-md hover:bg-muted"
              >
                {item.label}
              </Link>
            ))}

            <div className="relative group">
              <button className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground rounded-md hover:bg-muted flex items-center space-x-1">
                <span>More</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className="transition-transform duration-300 group-hover:rotate-180 text-muted-foreground group-hover:text-foreground"
                />
              </button>

              <div className="absolute right-0 mt-2 w-56 bg-card rounded-xl shadow-card border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="py-2">
                  {moreItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-foreground"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>}

          <div className="hidden lg:flex items-center space-x-3">
            {isPortalPage ? (
              <>
                <button
                  onClick={handlePortalLogout}
                  className="px-6 py-3 bg-slate-900 text-white font-heading font-semibold rounded-xl shadow-cta transition-all duration-300 text-sm md:text-base"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  href={hasUserSession ? '/my-portal' : '/user-login'}
                  className="px-4 py-2.5 border border-emerald-200 text-emerald-700 font-semibold rounded-xl bg-emerald-50/70 hover:bg-emerald-100 transition-all duration-300 text-sm"
                >
                  {hasUserSession ? 'My Portal' : 'User Login'}
                </Link>
                <Link
                  href="/get-started"
                  className="px-6 py-3 bg-accent text-accent-foreground font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 transition-all duration-300 text-sm md:text-base"
                >
                  Get ParkPing
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="lg:hidden p-2 text-foreground/80 hover:text-foreground"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={30} />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && !isPortalPage && (
        <div className="lg:hidden bg-background border-t border-border">
          <nav className="container mx-auto px-4 py-5 space-y-1.5">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block px-5 py-3.5 text-base font-medium text-foreground/90 hover:text-foreground hover:bg-muted rounded-xl"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            <div className="pt-4 border-t border-border">
              {moreItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-5 py-3.5 text-base font-medium text-foreground/90 hover:text-foreground hover:bg-muted rounded-xl"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="pt-5 space-y-3 pb-3">
              <Link
                href={hasUserSession ? '/my-portal' : '/user-login'}
                className="block w-full px-6 py-4 border border-emerald-200 text-emerald-700 text-center font-semibold rounded-xl bg-emerald-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {hasUserSession ? 'My Portal' : 'User Login'}
              </Link>
              <Link
                href="/get-started"
                className="block w-full px-6 py-4 bg-accent text-accent-foreground text-center font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get ParkPing
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
